import { useCallback, useEffect, useState } from 'react';
import { BarChart3, Eye, MousePointerClick, TrendingUp, UserPlus } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { EtapasFunil, ResumoVisitas } from '../../api/tipos';
import {
  Abas,
  Aviso,
  Cartao,
  Esqueleto,
  GraficoBarras,
  Indicador,
  Indicadores,
  Tabela,
  Td,
  Th,
  Vazio,
} from '../../componentes/ui';
import { Colunas, Pagina, TituloPagina } from '../../componentes/Pagina';
import { numero, plural, porcentagem } from '../../util/formato';
import css from './VisitasAdmin.module.css';

const PERIODOS = [
  { valor: '7', rotulo: '7 dias' },
  { valor: '30', rotulo: '30 dias' },
  { valor: '90', rotulo: '90 dias' },
  { valor: '365', rotulo: '1 ano' },
] as const;

type Periodo = (typeof PERIODOS)[number]['valor'];

/** Atualiza sozinho: no dia de um lançamento, o número muda a cada minuto. */
const INTERVALO_MS = 60_000;

/**
 * Propagação da página pública.
 *
 * A contagem é anônima por construção — o servidor guarda um hash diário de
 * IP + navegador, não o IP. Isso limita o que dá para saber (não há como seguir
 * a mesma pessoa entre dias) e é uma troca deliberada: o que se quer aqui é
 * saber se o site está sendo encontrado, não quem entrou nele.
 *
 * O número que responde essa pergunta não é "visitas": é a CONVERSÃO. Muita
 * visita sem cadastro significa que o tráfego chega e não serve — problema de
 * mensagem ou de público, não de volume.
 */
export function VisitasAdmin() {
  const [periodo, setPeriodo] = useState<Periodo>('30');
  const [dados, setDados] = useState<ResumoVisitas | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setErro(null);
      setDados(await api<ResumoVisitas>(`/admin/visitas?dias=${periodo}`));
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao carregar as visitas.',
      );
    }
  }, [periodo]);

  useEffect(() => {
    void carregar();
    const t = setInterval(() => void carregar(), INTERVALO_MS);
    return () => clearInterval(t);
  }, [carregar]);

  return (
    <Pagina>
      <TituloPagina
        titulo="Visitas"
        descricao="Quem chega à página pública, por onde e quantos viram conta. Contagem anônima, sem cookie de rastreamento."
        acoes={
          <Abas
            rotulo="Período"
            opcoes={PERIODOS}
            atual={periodo}
            aoMudar={setPeriodo}
          />
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      {!dados ? (
        !erro && <Carregando />
      ) : (
        <>
          <Indicadores>
            <Indicador
              rotulo="Visitantes no período"
              icone={<Eye />}
              valor={numero(dados.visitantes)}
              detalhe={`${plural(dados.visitas, 'visita')} no total`}
            />
            <Indicador
              rotulo="Hoje"
              icone={<BarChart3 />}
              valor={numero(dados.visitantesHoje)}
              detalhe={`${plural(dados.visitasHoje, 'visita')} hoje`}
              tom={dados.visitantesHoje > 0 ? 'ok' : undefined}
            />
            <Indicador
              rotulo="Contas criadas"
              icone={<UserPlus />}
              valor={numero(dados.cadastros)}
              detalhe="No mesmo período"
            />
            <Indicador
              rotulo="Conversão"
              icone={<TrendingUp />}
              valor={porcentagem(dados.conversao / 100)}
              detalhe="Visitantes que viraram conta"
              tom={dados.conversao >= 2 ? 'ok' : 'alerta'}
            />
          </Indicadores>

          <Funil funil={dados.funil} />

          <Cartao titulo="Visitantes por dia">
            {dados.porDia.length === 0 ? (
              <Vazio
                icone={<BarChart3 />}
                titulo="Nenhuma visita ainda"
                descricao="Assim que alguém abrir a página pública, o número aparece aqui."
              />
            ) : (
              <Grafico dados={dados.porDia} />
            )}
          </Cartao>

          <Colunas minimo={380} alinhar="start">
            <Ranking
              titulo="De onde vem o tráfego"
              vazio="Nada registrado ainda."
              nota="“direto” = digitou o endereço, veio de app ou o navegador não informou."
              itens={dados.origens.map((o) => ({
                rotulo: o.origem,
                valor: o.visitas,
              }))}
            />
            <Ranking
              titulo="Páginas mais abertas"
              vazio="Nada registrado ainda."
              itens={dados.paginas.map((p) => ({
                rotulo: p.caminho,
                valor: p.visitas,
              }))}
            />
          </Colunas>

          <Colunas minimo={380} alinhar="start">
            <Ranking
              titulo="Aparelhos"
              vazio="Nada registrado ainda."
              nota="Visitantes únicos no período, pelo tipo de aparelho."
              itens={dados.dispositivos.map((d) => ({
                rotulo: d.dispositivo,
                valor: d.visitantes,
              }))}
            />
            <Cliques itens={dados.cliques} />
          </Colunas>
        </>
      )}
    </Pagina>
  );
}

function Carregando() {
  return (
    <>
      <Indicadores>
        {[0, 1, 2, 3].map((i) => (
          <Esqueleto key={i} altura={104} raio={12} />
        ))}
      </Indicadores>
      <Esqueleto altura={240} raio={12} />
      <Esqueleto altura={240} raio={12} />
    </>
  );
}

/** "2026-10-09" → "09/10", sem passar por `Date` (fuso viraria o dia). */
function diaMes(dia: string): string {
  const [, mes, d] = dia.split('-');
  return `${d}/${mes}`;
}

/**
 * Barras por dia.
 *
 * O `GraficoBarras` da biblioteca é HTML/CSS puro, sem biblioteca de gráfico:
 * é uma série de números positivos com um valor por dia, e trazer uma
 * dependência de 100 kB para isso pesaria mais no carregamento do que o
 * gráfico inteiro.
 */
function Grafico({
  dados,
}: {
  dados: Array<{ dia: string; visitas: number; visitantes: number }>;
}) {
  return (
    <GraficoBarras
      legenda={['Visitas', 'Visitantes']}
      pontos={dados.map((d) => ({
        rotulo: diaMes(d.dia),
        valor: d.visitas,
        secundario: d.visitantes,
      }))}
    />
  );
}

function Ranking({
  titulo,
  itens,
  vazio,
  nota,
}: {
  titulo: string;
  itens: Array<{ rotulo: string; valor: number }>;
  vazio: string;
  nota?: string;
}) {
  const maximo = Math.max(...itens.map((i) => i.valor), 1);

  return (
    <Cartao titulo={titulo} descricao={nota}>
      {itens.length === 0 ? (
        <p className={css.nada}>{vazio}</p>
      ) : (
        <ul className={css.barras}>
          {itens.map((i) => (
            <li key={i.rotulo} className={css.barraItem}>
              <div className={css.barraLinha}>
                <span className={css.barraRotulo} title={i.rotulo}>
                  {i.rotulo}
                </span>
                <span className={`${css.barraValor} num`}>{numero(i.valor)}</span>
              </div>
              <span className={css.trilho} aria-hidden="true">
                <span
                  className={css.preenchimento}
                  style={{ width: `${(i.valor / maximo) * 100}%` }}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

const ETAPAS: Array<{ chave: keyof EtapasFunil; rotulo: string }> = [
  { chave: 'visitantes', rotulo: 'Visitaram' },
  { chave: 'cadastros', rotulo: 'Criaram conta' },
  { chave: 'confirmados', rotulo: 'Confirmaram o e-mail' },
  { chave: 'recarregaram', rotulo: 'Fizeram recarga' },
  { chave: 'usaram', rotulo: 'Usaram o serviço' },
];

/** `a` de `b`, em %, sem dividir por zero. */
function taxa(a: number, b: number): string {
  return b > 0 ? porcentagem(a / b) : '—';
}

/**
 * Funil de conversão: onde as pessoas param.
 *
 * Cada etapa mostra a taxa sobre a ANTERIOR, que é o que aponta o gargalo —
 * 50% de quem cria conta não confirma o e-mail é problema de e-mail, não de
 * tráfego. As etapas depois do cadastro são das contas criadas no período,
 * onde estão hoje.
 */
function Funil({ funil }: { funil: ResumoVisitas['funil'] }) {
  const { total } = funil;
  const maximo = Math.max(total.visitantes, total.cadastros, 1);

  return (
    <>
      <Cartao
        titulo="Funil de conversão"
        descricao="Cada etapa com a taxa sobre a anterior: a queda maior é o gargalo."
      >
        <ol className={css.funil}>
          {ETAPAS.map((etapa, i) => {
            const valor = total[etapa.chave];
            const anterior = i > 0 ? total[ETAPAS[i - 1].chave] : null;
            return (
              <li key={etapa.chave} className={css.etapa}>
                <span className={css.etapaRotulo}>{etapa.rotulo}</span>
                <span className={css.etapaTrilho} aria-hidden="true">
                  <span
                    className={css.etapaBarra}
                    style={{
                      width: `${Math.max(valor > 0 ? 1 : 0, (valor / maximo) * 100)}%`,
                    }}
                  />
                </span>
                <span className={`${css.etapaValor} num`}>
                  <strong>{numero(valor)}</strong>
                  {anterior !== null && (
                    <span className={css.etapaTaxa}>
                      {taxa(valor, anterior)} da anterior
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      </Cartao>

      {funil.porCanal.length > 0 && (
        <Cartao
          padding={0}
          titulo="Funil por canal"
          descricao={
            <>
              Canal = campanha (utm_source) ou site de onde a pessoa veio da primeira vez.
              “não informado” = conta anterior a esta medição ou de quem recusou a medição de
              audiência.
            </>
          }
        >
          <Tabela rotulo="Funil por canal">
            <thead>
              <tr>
                <Th>Canal</Th>
                <Th alinhar="direita">Visitantes</Th>
                <Th alinhar="direita">Contas</Th>
                <Th alinhar="direita">Confirmaram</Th>
                <Th alinhar="direita">Recarga</Th>
                <Th alinhar="direita">Usaram</Th>
                <Th alinhar="direita">Visita → conta</Th>
              </tr>
            </thead>
            <tbody>
              {funil.porCanal.map((c) => (
                <tr key={c.canal}>
                  <Td>{c.canal}</Td>
                  <Td alinhar="direita">{numero(c.visitantes)}</Td>
                  <Td alinhar="direita">{numero(c.cadastros)}</Td>
                  <Td alinhar="direita">{numero(c.confirmados)}</Td>
                  <Td alinhar="direita">{numero(c.recarregaram)}</Td>
                  <Td alinhar="direita">{numero(c.usaram)}</Td>
                  <Td alinhar="direita">{taxa(c.cadastros, c.visitantes)}</Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        </Cartao>
      )}
    </>
  );
}

/** Nomes dos alvos marcados com `data-clique`, para a tabela ler como gente. */
const ALVOS: Record<string, string> = {
  'criar-conta-topo': 'Criar conta (topo)',
  'criar-conta-chamada': 'Criar conta (chamada)',
  'criar-conta-precos': 'Criar conta (preços)',
  'criar-conta-final': 'Criar conta (fim da página)',
  'criar-conta-topo-guia': 'Criar conta (início do guia)',
  'criar-conta-fim-guia': 'Criar conta (fim do guia)',
  'entrar-topo': 'Entrar',
  'ver-precos-chamada': 'Ver preços',
  'ver-precos-guia': 'Ver preços (guia)',
  'nav-escritorios': 'Menu: Para escritórios',
  'nav-precos': 'Menu: Preços',
  'rodape-guia': 'Rodapé: guia',
  'guia-relacionado': 'Veja também (outro guia)',
};

/**
 * Cliques por página e botão. Com o número de visitantes de cada página, diz
 * qual delas convence: muito tráfego e pouco "Criar conta" é texto a revisar.
 */
function Cliques({
  itens,
}: {
  itens: Array<{ caminho: string; alvo: string; cliques: number }>;
}) {
  return (
    <Cartao
      padding={0}
      titulo="Cliques nos botões"
      descricao="Só de quem permitiu a medição de audiência. Sem identificar quem clicou."
    >
      {itens.length === 0 ? (
        <Vazio
          icone={<MousePointerClick />}
          titulo="Nenhum clique"
          descricao="Nenhum clique registrado no período."
        />
      ) : (
        <Tabela rotulo="Cliques nos botões">
          <thead>
            <tr>
              <Th>Página</Th>
              <Th>Botão</Th>
              <Th alinhar="direita">Cliques</Th>
            </tr>
          </thead>
          <tbody>
            {itens.map((c) => (
              <tr key={`${c.caminho}|${c.alvo}`}>
                <Td>{c.caminho}</Td>
                <Td>{ALVOS[c.alvo] ?? c.alvo}</Td>
                <Td alinhar="direita">{numero(c.cliques)}</Td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      )}
    </Cartao>
  );
}
